import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { Upload, X, Shield } from 'lucide-react'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { useAuthStore } from '@/store/authStore'
import { useToastStore } from '@/store/toastStore'
import { formatBytes } from '@/utils/format'

interface DropzoneProps {
  onFilesSelected: (files: File[]) => void
  onUpload: (options?: { password?: string; maxDownloads?: number; encrypted?: boolean }) => void
  selectedFiles: File[]
}

const FREE_MAX_SIZE = 50 * 1024 * 1024 // 50MB
const PREMIUM_MAX_SIZE = 2 * 1024 * 1024 * 1024 // 2GB
const BUSINESS_MAX_SIZE = 10 * 1024 * 1024 * 1024 // 10GB

export function Dropzone({ onFilesSelected, onUpload, selectedFiles }: DropzoneProps) {
  const [password, setPassword] = useState('')
  const [maxDownloads, setMaxDownloads] = useState<number | ''>('')
  const [showOptions, setShowOptions] = useState(false)
  const [encrypted, setEncrypted] = useState(false)
  const user = useAuthStore((state) => state.user)
  const addToast = useToastStore((state) => state.addToast)
  const tier = user?.tier || 'free'
  const isPremium = tier === 'premium' || tier === 'business'
  const isBusiness = tier === 'business'

  const getMaxSize = () => {
    if (isBusiness) return BUSINESS_MAX_SIZE
    if (isPremium) return PREMIUM_MAX_SIZE
    return FREE_MAX_SIZE
  }

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: unknown[]) => {
    // Check if any files were rejected due to size
    if (rejectedFiles && (rejectedFiles as Array<{file: File}>).length > 0) {
      const rejected = rejectedFiles as Array<{file: File, errors: Array<{code: string}>}>
      const hasOversizedFile = rejected.some(r => r.errors.some(e => e.code === 'file-too-large'))
      if (hasOversizedFile) {
        addToast('info', 'File too large for your plan. Upgrade for larger uploads.')
      }
    }
    if (acceptedFiles.length > 0) {
      onFilesSelected([...selectedFiles, ...acceptedFiles])
    }
  }, [selectedFiles, onFilesSelected, addToast])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: getMaxSize(),
  })

  const removeFile = (index: number) => {
    const newFiles = [...selectedFiles]
    newFiles.splice(index, 1)
    onFilesSelected(newFiles)
  }

  const totalSize = selectedFiles.reduce((acc, file) => acc + file.size, 0)

  return (
    <div className="card p-6">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 ${
          isDragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-primary-400'
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        {isDragActive ? (
          <p className="text-primary-600 font-medium">Drop files here...</p>
        ) : (
          <>
            <p className="text-gray-600 mb-2">
              Drag & drop files here, or click to select
            </p>
            <p className="text-sm text-gray-400">
              Max {isBusiness ? '10GB' : isPremium ? '2GB' : '50MB'} per file
              {!isBusiness && ' (upgrade for more)'}
            </p>
          </>
        )}
      </div>

      {selectedFiles.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">
              Selected Files ({selectedFiles.length})
            </h3>
            <span className="text-sm text-gray-500">{formatBytes(totalSize)}</span>
          </div>

          <div className="space-y-2 max-h-40 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
              >
                <div className="flex items-center min-w-0">
                  <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-sm text-gray-700 truncate">{file.name}</span>
                  <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                    {formatBytes(file.size)}
                  </span>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  aria-label={`Remove ${file.name}`}
                  className="p-1 hover:bg-gray-200 rounded ml-2"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            ))}
          </div>

          {isPremium && (
            <div className="mt-4">
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                {showOptions ? 'Hide options' : 'Show advanced options'}
              </button>

              {showOptions && (
                <div className="mt-3 space-y-3">
                  <Input
                    label="Password protection (optional)"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter a password"
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Download limit <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <select
                      value={maxDownloads}
                      onChange={(e) => setMaxDownloads(e.target.value ? Number(e.target.value) : '')}
                      className="input w-full"
                    >
                      <option value="">No limit</option>
                      <option value="1">1 download</option>
                      <option value="5">5 downloads</option>
                      <option value="10">10 downloads</option>
                      <option value="25">25 downloads</option>
                      <option value="100">100 downloads</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* E2E Encryption toggle — premium/business only */}
          {isPremium && (
            <div className="mt-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <button
                  type="button"
                  role="switch"
                  aria-checked={encrypted}
                  aria-label="End-to-end encrypt"
                  onClick={() => setEncrypted(!encrypted)}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    encrypted ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none absolute top-0.5 left-0.5 inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
                      encrypted ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
                <div className="flex items-center gap-2">
                  <Shield className={`w-4 h-4 ${encrypted ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className="text-sm font-medium text-gray-700">End-to-end encrypt</span>
                </div>
              </label>
              <p className={`text-xs mt-1 ml-[52px] ${encrypted ? 'text-green-600' : 'text-gray-400'}`}>
                {encrypted
                  ? 'Files are encrypted in your browser before upload. Only people with the link can decrypt.'
                  : 'Enable to encrypt files before they leave your browser.'}
              </p>
            </div>
          )}

          <Button
            className="w-full mt-4"
            onClick={() => onUpload({ password: password || undefined, maxDownloads: maxDownloads ? Number(maxDownloads) : undefined, encrypted })}
          >
            {encrypted ? 'Encrypt & Upload' : 'Upload'} {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}
          </Button>

          {tier === 'free' && selectedFiles.length > 0 && (
            <p className="text-xs text-gray-500 mt-3 text-center">
              <Link to="/pricing" className="text-primary-600 hover:text-primary-700 font-medium">Upgrade</Link>
              {' '}for password protection, E2E encryption & 2GB uploads
            </p>
          )}
        </div>
      )}
    </div>
  )
}
